<template>
  <div class="responses-from-recipients">
    <wtr-responses-from-recipients
      :list="recipientsList"
      :options="{
        totalElements: totalRecipientsList,
        pagination: true,
      }"
      :params="paginationParams"
      :showLoader="loading"
      @filter-change="handleFilterChange"
      @size-change="handleSizeChange"
      @page-change="handlePageChange"
    >
      <!-- Recipients Table -->
      <template v-slot:list-table>
        <b-table
          thead-class="bg-primary text-light"
          primary-key="assignmentId"
          outlined
          responsive
          hover
          :fields="tableFields"
          :items="recipientsList"
          show-empty
          empty-text="No assigned materials found"
          :busy="loading"
          :sort-by.sync="paginationParams.sort"
          :sort-desc.sync="paginationParams.sortDesc"
        >
          <!-- Headers -->
          <template v-slot:head(recipientName)="data">
            <div class="d-inline-flex align-items-center text-nowrap">
              <div class="text-nowrap">{{ data.label }}</div>
              <span
                class="cursor-pointer"
                v-on:click="handleSort('recipient_name')"
              >
                &nbsp;<i class="fa fa-sort ml-1" aria-hidden="true"></i>
              </span>
            </div>
          </template>
          <template v-slot:head(recipientEmail)="data">
            <div class="d-inline-flex align-items-center text-nowrap">
              <div class="text-nowrap">{{ data.label }}</div>
              <span
                class="cursor-pointer"
                v-on:click="handleSort('recipient_email')"
              >
                &nbsp;<i class="fa fa-sort ml-1" aria-hidden="true"></i>
              </span>
            </div>
          </template>
          <template v-slot:head(submittedDate)="data">
            <div class="d-inline-flex align-items-center text-nowrap">
              <div class="text-nowrap">{{ data.label }}</div>
              <span
                class="cursor-pointer"
                v-on:click="handleSort('submitted_date')"
              >
                &nbsp;<i class="fa fa-sort ml-1" aria-hidden="true"></i>
              </span>
            </div>
          </template>
          <template v-slot:head(actions)="data">
            <div class="text-nowrap">{{ data.label }}</div>
          </template>

          <!-- Cells -->
          <template v-slot:cell(recipientName)="row">
            <div class="text-nowrap fs-13">
              {{ row.item.recipientName || "N/A" }}
            </div>
          </template>
          <template v-slot:cell(recipientEmail)="row">
            <div class="text-nowrap fs-13">
              {{ row.item.recipientEmail || "N/A" }}
            </div>
          </template>
          <template v-slot:cell(submittedDate)="row">
            <div class="text-nowrap fs-13">
              {{ row.item.submittedDate ? $convertToUserTimezone(row.item.submittedDate) : "--" }}
            </div>
          </template>
          <template v-slot:cell(actions)="row">
            <table-actions
              :key="row.item.assignmentId"
              :viewIcon="{ show: 'View', title: 'View Assignment' }"
              :printIcon="{ show: 'Print', title: 'Print Response' }"
              :deleteIcon="{ show: 'Delete', title: 'Delete Response' }"
              @view="handlePdfAction(row.item, 'view')"
              @printIcon="handlePdfAction(row.item, 'print')"
              @delete="deleteRecipientResponse(row.item)"
            ></table-actions>
          </template>
        </b-table>
      </template>
      <template v-slot:empty>
        <tr>
          <td :colspan="tableFields.length" class="text-center py-5">
            <div class="text-muted">
              <i class="fas fa-users fa-2x mb-2"></i>
              <h5>No assigned materials found</h5>
              <p class="mb-0">No assignments have been created yet</p>
            </div>
          </td>
        </tr>
      </template>
    </wtr-responses-from-recipients>
  </div>
</template>

<script>
import TableActionsComponent from "../../../../components/common/table-actions.vue";
import WTRResponsesFromRecipients from "../../../../components/common/wtr-responses-from-recipients.vue";

export default {
  name: "ResponsesFromRecipients",
  components: {
    TableActions: TableActionsComponent,
    "wtr-responses-from-recipients": WTRResponsesFromRecipients,
  },
  props: {
    currentFolder: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      recipientsList: [],
      paginationParams: {
        page: 0,
        size: 10,
        sort: "assignment_date,desc",
        sortDesc: true,
      },
      totalRecipientsList: 0,
      loading: false,
      currentFilters: {
        search: "",
      },
    };
  },
  computed: {
    tableFields() {
      return [
        { key: "recipientName", label: this.$t("Recipient Name") },
        { key: "recipientEmail", label: this.$t("Recipient Email") },
        { key: "submittedDate", label: this.$t("Submitted Date") },
        { key: "actions", label: this.$t("Actions") },
      ];
    },
  },
  mounted() {
    this.fetchRecipientsList();
  },
  methods: {
    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },

    async fetchRecipientsList() {
      console.log("Fetching recipients list...", this.currentFolder);
      await this.sleep(100);

      this.loading = true;
      try {
        const queryParams = {
          page: this.paginationParams.page,
          size: this.paginationParams.size,
          sort: this.paginationParams.sort,
          sortDirection: this.paginationParams.sortDesc ? "desc" : "asc",
        };

        const name = this.currentFolder.name;
        const firstUnderscoreIndex = name.indexOf("_");

        const payloadSystemIdNumber = name.substring(0, firstUnderscoreIndex);
        const payloadAssignmentDate = name.substring(firstUnderscoreIndex + 1);

        const body = {
          ...this.currentFilters,
          systemIdNumber: payloadSystemIdNumber,
          assignmentDate: payloadAssignmentDate,
        };

        Object.keys(body).forEach((key) => {
          if (
            body[key] === null ||
            body[key] === "" ||
            body[key] === undefined
          ) {
            delete body[key];
          }
        });

        const response = await this.$api.getRecipientsList(queryParams, body);
        if (this.$handleResponse(response)) {
          const payload = response.data.payload;
          this.recipientsList = payload?.content || [];
          this.totalRecipientsList =
            payload?.totalElements || this.recipientsList.length;
          this.$emit("folder-changed");
        }
      } catch (error) {
        this.$handleError(error);
        this.recipientsList = [];
        this.totalRecipientsList = 0;
      } finally {
        this.loading = false;
      }
    },

    handleFilterChange(newFilters) {
      if (Object.keys(newFilters).length === 0) {
        this.currentFilters = {
          search: "",
        };
      } else {
        this.currentFilters = {
          ...this.currentFilters,
          ...newFilters,
        };
      }

      this.paginationParams.page = 0;
      this.fetchRecipientsList();
    },

    handleSizeChange(params) {
      this.paginationParams.size = params.size;
      this.paginationParams.page = 0;
      this.fetchRecipientsList();
    },

    handlePageChange(params) {
      this.paginationParams.page = params.page;
      this.fetchRecipientsList();
    },

    handleSort(sortField) {
      const currentSortField = this.paginationParams.sort.split(",")[0];

      if (currentSortField === sortField) {
        this.paginationParams.sortDesc = !this.paginationParams.sortDesc;
      } else {
        this.paginationParams.sortDesc = false;
      }

      const direction = this.paginationParams.sortDesc ? "desc" : "asc";
      this.paginationParams.sort = `${sortField},${direction}`;
      this.paginationParams.page = 0;

      this.fetchRecipientsList();
    },

    async handlePdfAction(item, action = "view") {
      if (!item.responsePdfPath) return;

      try {
        const response = await this.$api.viewResponseOfRecipient(
          item.recipientId
        );

        if (response.status !== 200) {
          throw new Error("Failed to fetch PDF");
        }

        const pdfBlob = new Blob([response.data], { type: "application/pdf" });
        const pdfUrl = URL.createObjectURL(pdfBlob);

        if (action === "print") {
          const printWindow = window.open(pdfUrl);

          if (!printWindow) {
            throw new Error("Could not open print window");
          }

          printWindow.addEventListener("load", () => {
            setTimeout(() => {
              printWindow.print();
              setTimeout(() => URL.revokeObjectURL(pdfUrl), 2000);
            }, 500);
          });
        } else {
          // Default to view
          window.open(pdfUrl, "_blank", "noopener,noreferrer");
          setTimeout(() => URL.revokeObjectURL(pdfUrl), 60000);
        }
      } catch (error) {
        console.error(
          `Error ${action === "print" ? "printing" : "viewing"} PDF:`,
          error
        );

        if (action === "print") {
          this.$toastr("error", "Failed to open PDF for printing");
        }

        this.$handleError(error);
      }
    },
    deleteRecipientResponse(item) {
      this.$deleteConfirmation(
        `Are you sure you want to delete the response from ${
          item.recipientName || item.recipientEmail
        }?`,
        async () => {
          try {
            const response = await this.$api.deleteResponseOfRecipient({
              recipientId: item.recipientId,
            });
            console.log("Delete response API response:", response);
            if (this.$handleResponse && !this.$handleResponse(response)) {
              return false;
            }
            this.$toastr("success", "Response deleted successfully");
            this.fetchRecipientsList();
          } catch (error) {
            console.error("Error deleting response:", error);
            this.$handleError(error);
          }
        }
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.fs-13 {
  font-size: 13px !important;
}
</style>
